import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../configs/translations'
import { resetCockpitFilter, setCockpitSelectedPartnerIds } from '../../model'
import { useCockpitFilterOptionsBubbleItems, useCockpitPartnersShown } from '../../model/hooks'
import { useCockpitFilterInitialization } from '../../model/hooks/useCockpitFilterInitialization'
import {
  selectCockpitPartnerData,
  selectCockpitRtm,
  selectCockpitSelectedPartnerIds,
  selectIsCockpitFilterDirty,
  selectIsCockpitFilterOptionsDirty,
} from '../../model/selectors'

import { CockpitFilterBubbleList } from './components/cockpitFilterBubbleList/CockpitFilterBubbleList'
import { CockpitFilterOptions } from './components/cockpitFilterOptions/CockpitFilterOptions'
import { CockpitSelectPartnersFilter } from './components/cockpitSelectPartnersFilter/CockpitSelectPartnersFilter'

import styles from './CockpitFilterPanel.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.filterPanel

type TFilterMode = 'filter' | 'select'

export const CockpitFilterPanel = () => {
  const dispatch = useAppDispatch()
  const selectedRtm = useSelector(selectCockpitRtm)
  const partnerData = useSelector(selectCockpitPartnerData)
  const selectedPartnerIds = useSelector(selectCockpitSelectedPartnerIds)
  useCockpitFilterInitialization()
  const isFilterDirty = useSelector(selectIsCockpitFilterDirty)
  const isFilterOptionsDirty = useSelector(selectIsCockpitFilterOptionsDirty)
  const {
    shownCount: shownFilteredCount,
    totalCount: shownTotal,
    textKey: shownTextKey,
  } = useCockpitPartnersShown()
  const [filterMode, setFilterMode] = useState<TFilterMode | null>(null)

  const rtmFilteredPartners = useMemo(
    () =>
      partnerData.filter((partner) =>
        selectedRtm === 'resellers' ? partner.IsCommercial === 1 : partner.IsDistributor === 1
      ),
    [partnerData, selectedRtm]
  )

  const filterOptionBubbleItems = useCockpitFilterOptionsBubbleItems()
  const selectedPartnerBubbleItems = useMemo(
    () =>
      selectedPartnerIds
        .map((id) => rtmFilteredPartners.find((partner) => partner.PartnerId === id))
        .filter((partner): partner is (typeof rtmFilteredPartners)[number] => Boolean(partner))
        .map((partner) => ({
          key: partner.PartnerId,
          label: partner.PartnerName,
          onRemove: () =>
            dispatch(
              setCockpitSelectedPartnerIds(
                selectedPartnerIds.filter((id) => id !== partner.PartnerId)
              )
            ),
        })),
    [selectedPartnerIds, rtmFilteredPartners, dispatch]
  )
  const bubbleItems = useMemo(
    () => [...filterOptionBubbleItems, ...selectedPartnerBubbleItems],
    [filterOptionBubbleItems, selectedPartnerBubbleItems]
  )

  const handleToggleMode = useCallback((mode: TFilterMode) => {
    setFilterMode((current) => (current === mode ? null : mode))
  }, [])

  const filterButtonTextKey = filterMode === 'filter' ? KEYS.hideFilters : KEYS.filterPartners
  const selectButtonTextKey = filterMode === 'select' ? KEYS.hideList : KEYS.selectPartners

  return (
    <div className={styles.root}>
      <div className={styles.shownSummary}>
        <Translation
          span
          text={shownTextKey}
          replaceParams={[
            { key: '{count}', value: `<strong>${shownFilteredCount}</strong>` },
            { key: '{totalCount}', value: String(shownTotal) },
          ]}
        />
      </div>
      <CockpitFilterBubbleList items={bubbleItems} />
      <div className={styles.modeToggle}>
        {selectedPartnerIds.length === 0 && (
          <Button
            variant="outlined"
            size="sm"
            className={styles.modeButton}
            active={filterMode === 'filter'}
            onClick={() => handleToggleMode('filter')}
          >
            <Translation span text={filterButtonTextKey} />
          </Button>
        )}
        {!isFilterOptionsDirty && (
          <Button
            variant="outlined"
            size="sm"
            className={styles.modeButton}
            active={filterMode === 'select'}
            onClick={() => handleToggleMode('select')}
          >
            <Translation span text={selectButtonTextKey} />
          </Button>
        )}
        {isFilterDirty && (
          <Button variant="text" size="sm" onClick={() => dispatch(resetCockpitFilter())}>
            <Translation span text={KEYS.clearSelection} />
          </Button>
        )}
      </div>
      {filterMode === 'filter' && (
        <div className={styles.expandedContent}>
          <div className={styles.divider} />
          <CockpitFilterOptions />
        </div>
      )}
      {filterMode === 'select' && (
        <div className={styles.expandedContent}>
          <div className={styles.divider} />
          <CockpitSelectPartnersFilter
            partners={rtmFilteredPartners}
            selectedPartnerIds={selectedPartnerIds}
            onChangeSelectedPartnerIds={(ids) => dispatch(setCockpitSelectedPartnerIds(ids))}
          />
        </div>
      )}
    </div>
  )
}
